import { mediaManifest } from './media-manifest.ts';

/**
 * Public origin the media bucket (`goalpost-media`) is served from. Hardcoded
 * because one bucket carries one custom domain, identical across environments —
 * see docs/media-storage.md.
 */
export const MEDIA_ORIGIN = 'https://media.movegoalpost.com';

export interface MediaAsset {
  /** Absolute URL the browser fetches the asset from. */
  url: string;
  contentType: string;
  bytes: number;
}

interface ManifestEntry {
  key: string;
  contentType: string;
  bytes: number;
}

const manifest: Record<string, ManifestEntry> = mediaManifest;

/**
 * Resolve a media id — an asset's `out/`-relative filename, e.g.
 * `overview-approval-rules-software-light.mp4` — to its served URL, or `null`
 * if it isn't in the manifest (e.g. before it has been synced). The manifest is
 * produced by `marketing:asset:push`; see docs/media-storage.md.
 */
export function media(id: string): MediaAsset | null {
  const entry = manifest[id];
  if (!entry) return null;
  return {
    url: `${MEDIA_ORIGIN}/${entry.key}`,
    contentType: entry.contentType,
    bytes: entry.bytes,
  };
}

/** One asset the media manifest declares: its stable id and the object it resolves to. */
export interface MediaManifestEntry {
  /** Stable id — the asset's `out/`-relative filename, e.g. `overview-teams-software-light.mp4`. */
  id: string;
  /** Content-hashed object key the id currently resolves to. */
  key: string;
  contentType: string;
  bytes: number;
}

/**
 * Every asset the manifest currently declares. The manifest names exactly one key
 * per id — the generation `marketing:asset:push` published last — so this is the set of
 * objects in use, as distinct from everything the bucket happens to hold. A
 * consumer needing a lookup builds it from these entries. See
 * docs/media-storage.md.
 */
export const mediaManifestEntries: readonly MediaManifestEntry[] = Object.entries(manifest).map(
  ([id, entry]) => ({
    id,
    key: entry.key,
    contentType: entry.contentType,
    bytes: entry.bytes,
  }),
);

/** A responsive image's `<img>` sources, resolved from the media manifest. */
export interface MediaImageSet {
  /** Absolute URL for the `<img src>` fallback (the widest rung). */
  src: string;
  /** `<img srcset>` value: every rung's URL with its `w` descriptor. */
  srcset: string;
}

// The `<img src>` fallback for browsers that ignore `srcset`: the widest rung at
// or below this width, so a non-srcset client fetches a sensible middle size
// rather than the heaviest rung. Real coverage comes from `srcset`; this is just
// the floor.
const IMAGE_SRC_FALLBACK_WIDTH = 1280;

/**
 * Resolve a base media id — an `image` target's `out/`-relative name without the
 * `-<width>.webp` suffix, e.g. `documents-software-light` — to the responsive WebP
 * set the marketing render emitted for it (the `{ src, srcset }` a docs `<img>`
 * needs), or `null` if no rungs are in the manifest yet (e.g. before
 * `marketing:asset:push`). The widths live in exactly one place — the marketing image
 * target (marketing/scripts/targets.ts) — and this reads whichever of them
 * reached the manifest, so the two can never drift out of a 404.
 */
export function mediaImageSet(base: string): MediaImageSet | null {
  const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const rungPattern = new RegExp(`^${escaped}-(\\d+)\\.webp$`);
  const rungs: Array<{ width: number; url: string }> = [];
  for (const [id, entry] of Object.entries(manifest)) {
    const match = id.match(rungPattern);
    if (match) rungs.push({ width: Number(match[1]), url: `${MEDIA_ORIGIN}/${entry.key}` });
  }
  if (rungs.length === 0) return null;
  rungs.sort((a, b) => a.width - b.width);
  // `src` fallback: the widest rung at or below the fallback width, else the
  // smallest (every rung is larger). `rungs` is sorted ascending.
  const belowFallback = rungs.filter((r) => r.width <= IMAGE_SRC_FALLBACK_WIDTH);
  const fallback = belowFallback.length > 0 ? belowFallback[belowFallback.length - 1] : rungs[0];
  return {
    src: fallback.url,
    srcset: rungs.map((r) => `${r.url} ${r.width}w`).join(', '),
  };
}
